import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Plus, Target, Handshake, Megaphone, ArrowsClockwise, Trash, Lightning,
  SpinnerGap, Compass, Check, Clock,
} from "@phosphor-icons/react";

const GOAL_ICONS = {
  "Generate leads or inquiries": Target,
  "Build trust and authority": Handshake,
  "Promote a specific offer or service": Megaphone,
  "Nurture existing audience or clients": ArrowsClockwise,
};

function goalIcon(goalText) {
  for (const [key, Icon] of Object.entries(GOAL_ICONS)) {
    if (goalText?.toLowerCase().includes(key.toLowerCase().split(" ")[0])) return Icon;
  }
  return Compass;
}

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }); }
  catch { return iso; }
}

export default function BriefsListPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const businessId = user?.active_business_id || "";

  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchBriefs = useCallback(async () => {
    try {
      const resp = await api.get(`/briefs?business_id=${businessId}`);
      setBriefs(resp.data.briefs || []);
    } catch (e) { console.error("Failed to fetch briefs:", e); }
    setLoading(false);
  }, [api, businessId]);

  useEffect(() => { fetchBriefs(); }, [fetchBriefs]);

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await api.delete(`/briefs/${deleteId}`);
      setBriefs(prev => prev.filter(b => b.brief_id !== deleteId));
      toast.success("Brief deleted");
    } catch {
      toast.error("Failed to delete brief");
    }
    setDeleting(false);
    setDeleteId(null);
  };

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-[var(--text-primary)] font-['Cormorant_Garamond']" data-testid="briefs-list-title">Content Briefs</h1>
            <p className="text-sm text-[var(--text-muted)] mt-1">AI-powered content strategies ready to generate</p>
          </div>
          <Button onClick={() => navigate("/briefs/new")} className="bg-[#FF4400] hover:bg-[#FF6633] text-white" data-testid="new-brief-btn">
            <Plus size={16} className="mr-1.5" /> New Brief
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><SpinnerGap size={28} className="animate-spin text-[#FF4400]" /></div>
        ) : briefs.length === 0 ? (
          <div className="text-center py-20" data-testid="briefs-empty-state">
            <Compass size={48} className="mx-auto text-[var(--text-muted)] opacity-30 mb-4" weight="duotone" />
            <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-2">No briefs yet</h2>
            <p className="text-sm text-[var(--text-muted)] mb-6 max-w-md mx-auto">
              Build a content brief to get AI-powered topic angles, tone recommendations, and strategic instructions before you generate.
            </p>
            <Button onClick={() => navigate("/briefs/new")} className="bg-[#FF4400] hover:bg-[#FF6633] text-white">
              <Plus size={16} className="mr-1.5" /> Build Your First Brief
            </Button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="briefs-list">
            {briefs.map((b, idx) => {
              const GoalIcon = goalIcon(b.answers?.goal);
              const isUsed = b.status === "used";
              return (
                <motion.div
                  key={b.brief_id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.03 }}
                  className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-4 hover:border-[var(--border-strong)] transition-colors group"
                  data-testid={`brief-card-${idx}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${isUsed ? "bg-emerald-500/10" : "bg-[#FF4400]/10"}`}>
                      <GoalIcon size={18} className={isUsed ? "text-emerald-400" : "text-[#FF4400]"} weight="duotone" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Link to={`/briefs/${b.brief_id}`} className="text-sm font-medium text-[var(--text-primary)] hover:text-[#FF4400] transition-colors truncate" data-testid={`brief-title-${idx}`}>
                          {b.title}
                        </Link>
                        <span className={`shrink-0 px-2 py-0.5 text-[9px] font-medium rounded-full ${isUsed ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" : "bg-amber-500/10 text-amber-400 border border-amber-500/20"}`} data-testid={`brief-status-${idx}`}>
                          {isUsed ? "Used" : "Draft"}
                        </span>
                      </div>
                      <p className="text-xs text-[var(--text-muted)] truncate" data-testid={`brief-angle-${idx}`}>
                        {b.generated_form_config?.suggested_angle || "No angle generated"}
                      </p>
                      <div className="flex items-center gap-3 mt-2 text-[10px] text-[var(--text-muted)]">
                        <span>{b.generated_form_config?.content_type || b.answers?.content_type}</span>
                        <span className="text-[var(--border)]">|</span>
                        <span className="flex items-center gap-1"><Clock size={10} /> {formatDate(b.created_at)}</span>
                        {isUsed && b.history_item_id && (
                          <>
                            <span className="text-[var(--border)]">|</span>
                            <Link to="/history" className="text-emerald-400 hover:underline flex items-center gap-1">
                              <Check size={10} /> View Content
                            </Link>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      {!isUsed && (
                        <Button variant="outline" size="sm" onClick={() => navigate(`/briefs/${b.brief_id}`)} className="text-xs border-[var(--border)] h-10 sm:h-7 px-2.5" data-testid={`use-brief-${idx}`}>
                          <Lightning size={12} className="mr-1" /> Use
                        </Button>
                      )}
                      <button onClick={() => setDeleteId(b.brief_id)} className="p-1.5 text-[var(--text-muted)] hover:text-red-400 transition-colors" data-testid={`delete-brief-${idx}`}>
                        <Trash size={14} />
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Delete confirmation */}
        <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <DialogContent className="bg-[var(--bg-raised)] border-[var(--border-strong)] text-[var(--text-primary)] max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-[var(--text-primary)]">Delete Brief?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-[var(--text-muted)]">This can't be undone.</p>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDeleteId(null)}>Cancel</Button>
              <Button onClick={handleDelete} disabled={deleting} className="bg-red-500 hover:bg-red-600 text-white">
                {deleting ? <SpinnerGap size={14} className="animate-spin mr-1" /> : null} Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
